// ============================================================================
// @orgazone/core-vouchers — registre unifié des BONS de valeur (SPEC §24.6ter B, matrice §24.6ter D).
// UTILITÉ : CLASSER et TRACER tous les bons par leur NATURE FISCALE — le critère qui ROUTE l'application :
//   • TITRE (paiement)  : chèque cadeau, avoir, acompte → de l'argent a été PAYÉ → règle le total (tender).
//   • REMISE (réduction): bon fidélité, coupon         → OFFERT           → réduit le prix (→ core-pricing).
//
// Cycle de vie : émis → consommé / PARTIEL (+ reliquat) / expiré. FENÊTRE DE VALIDITÉ (validFrom → expiresAt)
// → couvre l'avoir/cadeau (durée) ET les bons DATÉS, dont le BON ANNIVERSAIRE (valable le mois en cours).
// Anti-double-dépense : l'imputation est BORNÉE au solde (jamais de trop-perçu — on complète en CB/espèces).
//
// PUR TypeScript, ZÉRO dépendance : ce cœur ne fait que de l'ÉTAT et de la COMPARAISON — AUCUNE arithmétique
// à arrondir (la répartition d'une remise → core-pricing ; le paiement/fiscal → core-nf525 ; frontière
// §24.6ter D « ne recalcule jamais le prorata ni la TVA »). Persistance INJECTÉE : le registre `credit_note`
// (db-schema) est lu/écrit par l'app ; ce cœur RAISONNE sur des bons INJECTÉS.
//
// ⚠️ Multi-caisses : l'anti-double-dépense DÉFINITIF (même bon scanné hors ligne sur 2 caisses) = quarantaine
// à la remontée serveur (§13, sync) — hors du cœur pur, qui borne l'imputation d'UN bon à son solde.
// ============================================================================

/** Nature FISCALE d'un bon — route l'application (titre = paiement ; remise = réduction). */
export type VoucherNature = 'titre' | 'remise';

/** Type métier du bon. TITRES : avoir / cadeau / acompte · REMISES : fidelite / coupon. */
export type VoucherType = 'avoir' | 'cadeau' | 'acompte' | 'fidelite' | 'coupon';

/** État DÉRIVÉ d'un bon (jamais écrit — recalculé depuis les champs + l'heure). */
export type VoucherState = 'issued' | 'partial' | 'redeemed' | 'expired';

/**
 * COMMENT ON NOMME CES ÉTATS AU COMPTOIR — la même phrase en caisse et au back-office.
 *
 * ⚠️ UN LIBELLÉ DANS UN CŒUR PUR, ET C'EST ASSUMÉ. Ce ne sont pas des mots d'écran : c'est le
 * VOCABULAIRE d'un état métier, et deux surfaces qui montrent le même bon au même client ne peuvent
 * pas l'appeler autrement. Le back-office disait « En cours » là où la caisse disait « Disponible »
 * (relevé utilisateur 2026-09-06) — « en cours » ne dit d'ailleurs rien : en cours de quoi ?
 *
 * ⚠️ « PÉRIMÉ » ET NON « EXPIRÉ » : c'est le mot qu'on emploie devant un client.
 * ⚠️ « ENTAMÉ » EXISTE, et c'est le plus important : un chèque cadeau partiellement utilisé n'est ni
 * disponible ni utilisé — le confondre avec l'un des deux fausse l'engagement du magasin.
 */
export const VOUCHER_STATE_LABELS: Readonly<Record<VoucherState, string>> = {
  issued: 'Disponible',
  partial: 'Entamé',
  redeemed: 'Utilisé',
  expired: 'Périmé',
};

// Classification par nature — SOURCE UNIQUE (le critère fiscal « a-t-on payé pour ce bon ? »).
const TITRE_TYPES: ReadonlySet<VoucherType> = new Set<VoucherType>(['avoir', 'cadeau', 'acompte']);
const DISCOUNT_TYPES: ReadonlySet<VoucherType> = new Set<VoucherType>(['fidelite', 'coupon']);

/**
 * Un bon (titre ou remise). VALEUR = € (`amountCents`) OU % (`discountPctBp`, remise uniquement).
 * `remainingCents` = solde restant après consommation(s) partielle(s) d'un bon en € ; absent = plein.
 * `validFrom` / `expiresAt` = fenêtre de validité (epoch ms) : avoir/cadeau (durée), BON ANNIVERSAIRE
 * (1ᵉʳ → fin du mois), saisonnier… `redeemed` = consommé totalement (marqueur explicite).
 */
export interface Voucher {
  id: string;
  type: VoucherType;
  amountCents?: number; // valeur € (titre, avoir, acompte, coupon en €)
  discountPctBp?: number; // valeur % (remise : coupon/bon fidélité en %) — 1000 = 10 %
  remainingCents?: number; // solde € restant (défaut = amountCents)
  validFrom?: number; // début de validité (epoch ms) — ex. bon anniversaire = 1ᵉʳ du mois
  expiresAt?: number; // fin de validité (epoch ms)
  redeemed?: boolean; // consommé totalement
}

/**
 * Nature fiscale d'un TYPE de bon — la même règle, quand on n'a que le type sous la main.
 *
 * ⚠️ ELLE EXISTE POUR ÊTRE APPELÉE AU SCAN. L'écran de paiement ne dispose que du type rendu par la
 * base, et il le classait par une cascade écrite à la main dont le REPLI était « Avoir » : le jour où
 * un bon de FIDÉLITÉ entre dans la table des titres, il aurait été imputé comme un règlement. Une
 * remise comptée en paiement fausse la ventilation TVA **et** le Z — le défaut ne se voit pas à
 * l'écran, il se voit au contrôle. La question « titre ou remise ? » ne se répond donc qu'ici.
 */
export function natureOfType(type: VoucherType): VoucherNature {
  if (TITRE_TYPES.has(type)) return 'titre';
  if (DISCOUNT_TYPES.has(type)) return 'remise';
  throw new Error(`core-vouchers: type de bon inconnu « ${type} »`);
}

/** Nature fiscale du bon (route l'application : titre → paiement, remise → core-pricing). */
export function classify(v: Voucher): VoucherNature {
  return natureOfType(v.type);
}

/** Solde disponible en € (titre / coupon €). `undefined` si le bon porte une valeur en % (non monétaire). */
export function balanceCents(v: Voucher): number | undefined {
  if (v.amountCents === undefined) return undefined; // bon en % → pas de solde monétaire
  return v.remainingCents ?? v.amountCents;
}

/** Vrai si `nowMs` est dans la fenêtre de validité [validFrom, expiresAt] (bornes incluses). */
export function isWithinValidity(v: Voucher, nowMs: number): boolean {
  if (v.validFrom !== undefined && nowMs < v.validFrom) return false;
  if (v.expiresAt !== undefined && nowMs > v.expiresAt) return false;
  return true;
}

/** État dérivé (issued / partial / redeemed / expired) à l'instant `nowMs`. */
export function voucherState(v: Voucher, nowMs: number): VoucherState {
  const bal = balanceCents(v);
  if (v.redeemed || bal === 0) return 'redeemed';
  if (v.expiresAt !== undefined && nowMs > v.expiresAt) return 'expired';
  if (bal !== undefined && v.amountCents !== undefined && bal < v.amountCents) return 'partial';
  return 'issued';
}

/**
 * LA VALEUR QUE LE CLIENT DÉTIENT ENCORE EN TITRES — Σ des soldes de tout ce qui n'est pas consommé.
 *
 * ⚠️ LES PÉRIMÉS COMPTENT (décision utilisateur) : un titre périmé reste une valeur que le client a payée
 *    ou reçue, et que le commerçant peut choisir d'honorer. Seuls les titres CONSOMMÉS en sortent.
 * ⚠️ ELLE VIT ICI PARCE QU'ELLE ÉTAIT ÉCRITE DEUX FOIS : la caisse comptait les périmés, le back-office
 *    non — la même fiche annonçait deux valeurs. L'état vient de `voucherState`, déjà calculé par l'appelant.
 */
export function outstandingVoucherCents(vouchers: readonly { state: VoucherState; remainingCents: number }[]): number {
  return vouchers.reduce((s, v) => (v.state === 'redeemed' ? s : s + v.remainingCents), 0);
}

export interface VoucherValidation {
  ok: boolean;
  reason?: 'not_yet_valid' | 'expired' | 'redeemed' | 'no_balance';
}

/** Le bon est-il UTILISABLE à l'instant `nowMs` ? (dans la fenêtre, non consommé, solde > 0 pour un € ). */
export function validate(v: Voucher, nowMs: number): VoucherValidation {
  if (v.validFrom !== undefined && nowMs < v.validFrom) return { ok: false, reason: 'not_yet_valid' };
  if (v.expiresAt !== undefined && nowMs > v.expiresAt) return { ok: false, reason: 'expired' };
  if (v.redeemed) return { ok: false, reason: 'redeemed' };
  const bal = balanceCents(v);
  if (bal !== undefined && bal <= 0) return { ok: false, reason: bal === 0 ? 'redeemed' : 'no_balance' };
  return { ok: true };
}

export interface RedeemResult {
  appliedCents: number; // montant réellement imputé (BORNÉ au solde — anti-trop-perçu)
  remainingCents: number; // solde restant APRÈS imputation (reliquat si > 0)
  fullyRedeemed: boolean; // solde tombé à 0
}

/**
 * Consomme `amountCents` sur un bon en € (titre / coupon €). ANTI-DOUBLE-DÉPENSE : l'imputation est BORNÉE
 * au solde (« jamais de trop-perçu sur un titre », §7.1) → le reste est à régler autrement (CB/espèces).
 * Un reliquat > 0 laisse le bon consommable (état `partial`). Retourne le résultat PUR ; l'appelant persiste
 * le nouveau solde. Lève si le bon n'est pas utilisable, ou s'il porte une valeur en % (la réduction d'une
 * remise est appliquée par core-pricing, jamais « consommée » ici — frontière §24.6ter D).
 */
export function redeem(v: Voucher, amountCents: number, nowMs: number): RedeemResult {
  if (!Number.isSafeInteger(amountCents) || amountCents <= 0) {
    throw new Error('core-vouchers: montant à imputer entier > 0 attendu');
  }
  const val = validate(v, nowMs);
  if (!val.ok) throw new Error(`core-vouchers: bon non utilisable (${val.reason})`);
  const bal = balanceCents(v);
  if (bal === undefined) {
    throw new Error('core-vouchers: redeem porte sur un bon en € ; une remise % est appliquée par core-pricing');
  }
  const appliedCents = Math.min(amountCents, bal);
  const remainingCents = bal - appliedCents;
  return { appliedCents, remainingCents, fullyRedeemed: remainingCents === 0 };
}


// ── LE SOLDE SE DÉRIVE DU GRAND LIVRE ────────────────────────────────────────────────────────────
//
// 🔴 POURQUOI. Le solde restant était ÉCRIT par la caisse : elle lisait sa copie, retranchait, et
// poussait un instantané de la ligne. Sur un canal en dernier-écrivain-gagne, deux caisses qui
// consomment le même titre avant de s'être synchronisées produisent deux instantanés — le dernier
// gagne, et **une consommation est perdue**. Un solde qui résulte d'une SUITE d'événements ne se
// stocke pas : il se rejoue. Deux caisses hors ligne produisent alors deux LIGNES, qui s'ADDITIONNENT.
//
// ⚠️ MÊME RAISONNEMENT QUE LA CAGNOTTE, qui est dérivée de son grand livre depuis longtemps. Ce qui
// manquait aux titres n'était pas l'idée, c'était le grand livre.

/** Le SENS d'un mouvement — il ne se déduit pas des montants (voir ci-dessous). */
export type VoucherMovementKind = 'consume' | 'restore';

/** Un mouvement du grand livre d'un bon. */
export interface VoucherMovement {
  kind: VoucherMovementKind;
  /**
   * Montant du mouvement, en centimes.
   *
   * ⚠️ 🔴 ZÉRO POUR UN BON EN POURCENTAGE, et c'est tout l'enjeu : il ne consomme aucun euro, il se
   * consomme À L'UNITÉ. C'est pourquoi le sens est porté par `kind` et jamais déduit d'un montant —
   * sans lui, une consommation et une restitution seraient la même ligne.
   */
  amountCents: number;
}

/** Ce que le grand livre dit d'un bon, à cet instant. */
export interface DerivedVoucher {
  /** Solde restant en €. `undefined` pour un bon en % — il n'a pas de solde monétaire. */
  remainingCents?: number;
  /** Le bon est-il MORT (entièrement consommé) ? */
  redeemed: boolean;
  /** Nombre d'utilisations nettes — ce qui tue un bon en %, et qui éclaire un titre partiel. */
  uses: number;
}

/**
 * DÉRIVE l'état d'un bon de sa valeur émise et de ses mouvements. Fonction PURE : aucune lecture.
 *
 * ⚠️ 🔴 DEUX FAMILLES, DEUX RÈGLES DE MORT, et les confondre éteindrait des bons valides :
 *   · un bon à MONTANT (avoir, chèque cadeau, acompte, bon fidélité en €) meurt à SOLDE ZÉRO ;
 *   · un bon en POURCENTAGE n'a pas de solde — il meurt à sa PREMIÈRE utilisation, parce qu'il porte
 *     un numéro unique et se consomme à l'unité. Lui appliquer « solde 0 = mort » le tuerait dès
 *     l'émission, puisque son solde vaut zéro depuis toujours.
 *
 * ⚠️ LE SOLDE EST BORNÉ AUX DEUX BOUTS : jamais négatif (on n'impute pas plus que le bon ne vaut),
 * jamais au-dessus de la valeur émise (une annulation RESTITUE, elle ne crédite pas). Sans ces bornes,
 * un rejeu ou une annulation en double fabriquerait de l'argent.
 *
 * ⚠️ UNE RESTITUTION N'EFFACE PAS LA CONSOMMATION : « consommé puis rendu » n'est pas « jamais
 * consommé ». Les deux lignes restent, et c'est leur SOMME qui fait l'état — c'est ce qui rend
 * l'histoire relisible à un contrôle.
 */
export function deriveFromLedger(v: Voucher, movements: readonly VoucherMovement[]): DerivedVoucher {
  let consumedCents = 0;
  let restoredCents = 0;
  let useCount = 0;
  for (const m of movements) {
    if (!Number.isSafeInteger(m.amountCents) || m.amountCents < 0) {
      throw new Error('core-vouchers: mouvement en centimes entiers ≥ 0 attendu');
    }
    if (m.kind === 'consume') { consumedCents += m.amountCents; useCount += 1; }
    else { restoredCents += m.amountCents; useCount -= 1; }
  }
  const uses = Math.max(0, useCount);

  // Bon en POURCENTAGE : aucun solde monétaire, la mort vient de l'usage.
  if (v.amountCents === undefined) return { remainingCents: undefined, redeemed: uses > 0, uses };

  const grossCents = v.amountCents - consumedCents + restoredCents;
  const remainingCents = Math.min(v.amountCents, Math.max(0, grossCents));
  return { remainingCents, redeemed: remainingCents === 0, uses };
}

// La DURÉE DE VALIDITÉ réglée par le magasin → une date d'échéance (module dédié : c'est une règle
// de calendrier, pas d'état, et la caisse comme le back-office doivent la lire au même endroit).
export * from './validity';
